import { useState } from "react"
import { router } from "@inertiajs/react"
import { Boxes, Plus, Trash2 } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import {
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { PageHeader } from "@/components/ui/headers"
import { InlineInput } from "@/components/ui/inline-input"
import { StatusBadge } from "@/components/ui/status-badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
} from "@/components/ui/table"
import { ItemStockForm } from "@/features/staff/item-stock/item-stock-form"
import {
  ITEM_STOCK_PATHS,
  itemStockStatus,
  itemStockTone,
  tItemStock,
  type CountableItemOption,
  type ItemStockErrors,
  type ItemStockRow,
  type ItemStockStatus,
} from "@/features/staff/item-stock/item-stock"
import { patchInline } from "@/lib/inline-update"

export type ItemStockWorkspaceProps = {
  stocks: ItemStockRow[]
  countableItems: CountableItemOption[]
  errors?: ItemStockErrors
}

function statusLabel(status: ItemStockStatus): string {
  return tItemStock(`status_${status}`)
}

function ItemStockTableRow({
  stock,
  onDelete,
}: {
  stock: ItemStockRow
  onDelete: () => void
}) {
  const status = itemStockStatus(stock)

  return (
    <TableRow data-testid={`item-stock-row-${stock.id}`}>
      <TableRowHeader className="min-w-48">
        <span className="flex items-center gap-2">
          {stock.itemEmoji && (
            <span aria-hidden="true" className="text-base leading-none">
              {stock.itemEmoji}
            </span>
          )}
          <span className="truncate text-sm font-medium">{stock.itemName}</span>
        </span>
      </TableRowHeader>

      <TableCell className="min-w-32">
        <InlineInput
          value={String(stock.quantity)}
          inputMode="numeric"
          aria-label={tItemStock("quantity_label")}
          data-testid={`item-stock-quantity-${stock.id}`}
          onSubmit={(value) =>
            patchInline(ITEM_STOCK_PATHS.itemStock(stock.id), { quantity: value })
          }
        />
      </TableCell>

      <TableCell className="min-w-32">
        <InlineInput
          value={String(stock.lowThreshold)}
          inputMode="numeric"
          aria-label={tItemStock("low_threshold_label")}
          data-testid={`item-stock-threshold-${stock.id}`}
          onSubmit={(value) =>
            patchInline(ITEM_STOCK_PATHS.itemStock(stock.id), { low_threshold: value })
          }
        />
      </TableCell>

      <TableCell>
        <StatusBadge
          tone={itemStockTone(status)}
          label={statusLabel(status)}
          data-testid={`item-stock-status-${stock.id}`}
        />
      </TableCell>

      <TableCell className="text-right">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onDelete}
          aria-label={tItemStock("delete")}
          data-testid={`item-stock-delete-${stock.id}`}
        >
          <Trash2 className="size-4" />
        </Button>
      </TableCell>
    </TableRow>
  )
}

/**
 * Dish-level "existencias": how many orders of each tracked dish a manager can
 * still sell today.
 *
 * ## Not every dish is here
 *
 * Only dishes a manager chose to count get a row (`ItemStockForm`, in a
 * drawer — mirroring `SupplyForm`, since starting to track a dish is a setup
 * decision made once, not something retyped mid-shift). An item with no row
 * sells without limit — the feature narrows *which* dishes are capped, it
 * does not cap the whole menu the moment it is switched on.
 *
 * ## Both numbers commit inline; choosing the dish does not
 *
 * `quantity` and `lowThreshold` are retyped mid-shift, so they are
 * `InlineInput` on the row. Which dish gets a row keeps the drawer's explicit
 * submit — see `ItemStockForm`'s own note.
 *
 * ## The empty state points at the same drawer as the header button
 *
 * Both open `ItemStockForm`; the empty state does not invent a second path.
 */
export function ItemStockWorkspace({ stocks, countableItems }: ItemStockWorkspaceProps) {
  const [creating, setCreating] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<ItemStockRow | null>(null)

  const confirmDelete = () => {
    if (!pendingDelete) return

    router.delete(ITEM_STOCK_PATHS.itemStock(pendingDelete.id), {
      preserveScroll: true,
      onFinish: () => setPendingDelete(null),
    })
  }

  const canAdd = countableItems.length > 0

  return (
    <MainContainer size="md">
      <div className="space-y-6">
        <PageHeader
          title={tItemStock("title")}
          actions={
            canAdd ? (
              <Button type="button" onClick={() => setCreating(true)} data-testid="item-stock-new">
                <Plus className="size-4" />
                {tItemStock("new")}
              </Button>
            ) : undefined
          }
        />

        {stocks.length === 0 ? (
          <Card data-testid="item-stocks-empty">
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
              <Boxes className="text-muted-foreground size-8" aria-hidden="true" />
              <p className="text-sm font-semibold">{tItemStock("empty_title")}</p>
              <p className="text-muted-foreground max-w-md text-sm">{tItemStock("empty_hint")}</p>
              {canAdd && (
                <Button
                  type="button"
                  onClick={() => setCreating(true)}
                  data-testid="item-stock-new-empty"
                >
                  <Plus className="size-4" />
                  {tItemStock("empty_action")}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card density="compact" data-testid="item-stocks-list">
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tItemStock("table_item")}</TableHead>
                    <TableHead>{tItemStock("table_quantity")}</TableHead>
                    <TableHead>{tItemStock("table_threshold")}</TableHead>
                    <TableHead>{tItemStock("table_status")}</TableHead>
                    <TableHead className="text-right">{tItemStock("table_actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stocks.map((stock) => (
                    <ItemStockTableRow
                      key={stock.id}
                      stock={stock}
                      onDelete={() => setPendingDelete(stock)}
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      <ResponsiveDrawer open={creating} onOpenChange={setCreating}>
        <DrawerContent className="data-[vaul-drawer-direction=right]:sm:max-w-md">
          <DrawerHeader>
            <DrawerTitle>{tItemStock("new_title")}</DrawerTitle>
          </DrawerHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            <ItemStockForm countableItems={countableItems} onDone={() => setCreating(false)} />
          </div>
        </DrawerContent>
      </ResponsiveDrawer>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => !next && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tItemStock("delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>{tItemStock("delete_description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="item-stock-delete-cancel">
              {tItemStock("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="item-stock-delete-confirm">
              {tItemStock("delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainContainer>
  )
}
